import axios from 'axios';
import { endPoint } from '../../EndPoints/endPoint';

// api/general/agentmaster/* wraps every response the same way, confirmed live:
// { status: boolean, data: <payload or error string>, message: string|null }
export const isAgentApiSuccess = (data) => Boolean(data?.status === true);

export const unwrapAgentError = (data) =>
  (typeof data?.data === 'string' && data.data) || data?.message || 'Something went wrong. Please try again.';

// getlist / loaddropdownsandgrids.agents rows are confirmed to key off broker_Id;
// kept defensive for the other spellings this app's masters use elsewhere too.
export const resolveBrokerId = (row = {}) => {
  const direct = row?.broker_Id ?? row?.brokerId ?? row?.BrokerId ?? row?.Broker_Id ?? row?.id ?? row?.Id ?? row?.ID ?? '';
  return direct !== null && direct !== undefined ? String(direct).trim() : '';
};

// active/isSupplier come back as "1"/"0" strings (or null) from the API, not booleans.
export const toAgentBool = (v) => v === true || v === '1' || v === 1 || v === 'true';

export const pickAgentField = (row, ...keys) => {
  for (const key of keys) {
    const val = row?.[key];
    if (val !== null && val !== undefined && String(val).trim() !== '' && val !== '0') {
      return String(val).trim();
    }
  }
  return '';
};

// Confirmed live shape: salesPersons/suppliers are [{id, name}], brokerNames is
// a flat array of strings.
export const toAgentOption = (item) => {
  if (item === null || item === undefined) return null;
  if (typeof item === 'string' || typeof item === 'number') {
    return { value: item, label: String(item) };
  }
  const label = item?.name ?? item?.text ?? item?.label ?? item?.Name ?? item?.value ?? '';
  const value = item?.id ?? item?.value ?? item?.Id ?? item?.code ?? label;
  return { value, label: String(label) };
};

// Confirmed live shape: data.propertyPercentages is [{recordId, propertyNo, propertyName, per}] -
// the full property list (all zero `per` until an agent has saved rows against them),
// bundled into this same call rather than a separate properties dropdown.
export const toPropertyOption = (item) => ({
  value: String(item?.propertyNo ?? ''),
  text: item?.propertyName ?? '',
  recordId: item?.recordId ?? 0,
  per: item?.per ?? 0,
});

export const fetchAgentDropdownsAndGrids = async (apiService) => {
  const res = await apiService.get('AGENT_MASTER_DROPDOWNS');
  const data = res?.data?.data || {};
  return {
    salesPersons: (data.salesPersons || []).map(toAgentOption).filter(Boolean),
    suppliers: (data.suppliers || []).map(toAgentOption).filter(Boolean),
    brokerNames: data.brokerNames || [],
    properties: (data.propertyPercentages || []).map(toPropertyOption),
  };
};

// getbyid returns { header, attachments, propertyPercentages } - header uses the
// same field names as the save/update request body (name, address, landlineNo,
// mobileNo, email, intCommission, isSupplier, limit, type, ...), unlike
// getlist/loaddropdownsandgrids.agents which use the older broker_* column names.
export const fetchAgentById = async (apiService, brokerId) => {
  const id = String(brokerId || '').trim();
  if (!id) throw new Error('Agent id is required');
  const res = await apiService.getApiParamater('AGENT_MASTER_GET_BY_ID', `/${encodeURIComponent(id)}`);
  if (!isAgentApiSuccess(res?.data)) {
    throw new Error(unwrapAgentError(res?.data));
  }
  return res?.data?.data || {};
};

// Adapted from customerDocumentHelpers.js's uploadCustomerDocuments - same generic
// FileUpload endpoint, same upload-then-reference-the-filename flow.
export const uploadAgentAttachments = async (refNo, attachmentsList) => {
  const pending = (attachmentsList || []).filter((item) => item?.file instanceof File);
  if (!pending.length) {
    return (attachmentsList || []).map((item) => ({ ...item, filename: item.filename || '' }));
  }

  const formData = new FormData();
  pending.forEach(({ file }) => formData.append('files', file));
  formData.append('refNo', String(refNo));
  formData.append('fileCategory', 'AgentDocuments');

  const token = localStorage.getItem('Token');
  const db = localStorage.getItem('DataBase');

  const response = await axios.post(endPoint.UNIT_FILE, formData, {
    headers: {
      accept: 'text/plain',
      apikey: 'V+kBloEwqqVho1PINmQI0VJQwSBX2l/e',
      Authorization: `Bearer ${token}`,
      db,
    },
  });

  const results = response?.data?.result || [];
  if (!response?.data?.isSuccessful && !results.length) {
    throw new Error(response?.data?.message || 'Attachment upload failed');
  }

  let uploadIndex = 0;
  return (attachmentsList || []).map((item) => {
    if (item?.file instanceof File) {
      const uploaded = results[uploadIndex++] || {};
      const filePath = uploaded.filePath || uploaded.fileName || '';
      return { ...item, filename: filePath, url: uploaded.fullUrl || '', file: null };
    }
    return { ...item, filename: item.filename || '' };
  });
};
